/**
 * src/pricingtable/PricingCompareTable2.jsx — 2026-03-26
 * ------------------------------------------------------------------
 * PURPOSE
 * ------------------------------------------------------------------
 * WordPress-aligned detailed comparison table for the new app pricing page.
 *
 * WHY THIS FILE EXISTS
 * ------------------------------------------------------------------
 * Your WordPress pricing page already renders a richer compare section with:
 *   • a plan-summary row
 *   • category-grouped expandable sections
 *   • tooltip-enabled feature labels
 *   • featured-plan treatment for Pro
 *   • interval-aware price display in the summary row
 *
 * This file recreates that structure inside the app using the same merged
 * pricing presentation payload that WordPress already consumes.
 *
 * DATA CONTRACT
 * ------------------------------------------------------------------
 * Expects a pricingPresentation object shaped like your generated payload:
 * {
 *   plans: [...],
 *   compare: [...]
 * }
 *
 * IMPORTANT
 * ------------------------------------------------------------------
 * This file is PRESENTATION-ONLY.
 *
 * It does NOT:
 *   • change Stripe logic
 *   • trigger billing actions directly
 *   • own seat changes
 *
 * It can hand plan CTA clicks upward via onPlanSelect(plan), but it remains
 * a read-first comparison surface.
 *
 * DESIGN GOALS
 * ------------------------------------------------------------------
 * 1. Match the current WordPress compare section as closely as possible.
 * 2. Reuse the same pricing payload already driving the cards.
 * 3. Keep interval display in sync with the page toggle.
 * 4. Preserve enterprise-safe semantics for self-serve vs contact-sales.
 * 5. Add logs for local testing.
 */

import React, { useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";

import { planConfigs } from "plan-catalog";
import { getPlanCtaState } from "../utils/getPlanCtaState";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */

const PLAN_ORDER = ["standard", "pro", "enterprise"];

function getDisplayedSeatPrice(interval, perSeat) {
  if (perSeat == null) return null;

  const numeric = Number(perSeat);
  if (!Number.isFinite(numeric)) return null;

  if (interval === "annual") {
    return numeric / 12;
  }

  return numeric;
}

function formatMoneyParts(num) {
  if (num == null || Number.isNaN(Number(num))) {
    return ["–", ""];
  }

  const fixed = Number(num).toFixed(2);
  const [major, minor] = fixed.split(".");
  return [major, minor || "00"];
}

function getPlanPriceIdForInterval(planKey, interval) {
  const cfg = planConfigs?.[planKey] || {};
  const tier = cfg?.pricing?.[interval] || {};
  return tier?.priceId || null;
}

function getPlanByKey(plans, planKey) {
  return (Array.isArray(plans) ? plans : []).find((plan) => plan?.key === planKey) || null;
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      focusable="false"
      aria-hidden="true"
      style={{
        width: 18,
        height: 18,
        display: "block",
        fill: "currentColor",
      }}
    >
      <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      focusable="false"
      aria-hidden="true"
      className="pricing-compare-table2__chevron"
      style={{
        width: 18,
        height: 18,
        display: "block",
        fill: "#64748b",
        transition: "transform .18s ease",
      }}
    >
      <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
    </svg>
  );
}

/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */
const PricingCompareTable2 = ({
  pricingPresentation,
  pricingInterval,
  onPlanSelect,
  currentSubscription,
  context,
  seatCount,
}) => {
  const effectiveInterval =
    pricingInterval === "monthly" || pricingInterval === "annual"
      ? pricingInterval
      : "annual";

  const plans = useMemo(() => {
    const rawPlans = Array.isArray(pricingPresentation?.plans)
      ? pricingPresentation.plans
      : [];

    const orderedPlans = PLAN_ORDER
      .map((planKey) => getPlanByKey(rawPlans, planKey))
      .filter(Boolean);

    const normalizedPlans = orderedPlans.map((plan) => {
      const activePricing = plan?.pricing?.[effectiveInterval] || {};

      const normalizedPlan = {
        ...plan,

        /* --------------------------------------------------------
         * Active interval metadata
         * ------------------------------------------------------ */
        interval: effectiveInterval,
        displayedSeatPrice: getDisplayedSeatPrice(
          effectiveInterval,
          activePricing?.perSeat ?? null,
        ),

        /* --------------------------------------------------------
         * Active interval billing fields
         *
         * IMPORTANT:
         * - Keep these aligned with PricingTable2 so compare-table plan
         *   selection produces the exact same billing-ready object shape.
         * ------------------------------------------------------ */
        priceId: activePricing?.priceId || null,
        flatFee:
          Number.isFinite(Number(activePricing?.flatFee)) ?
            Number(activePricing.flatFee) :
            0,
        perSeat:
          Number.isFinite(Number(activePricing?.perSeat)) ?
            Number(activePricing.perSeat) :
            null,

        /* --------------------------------------------------------
         * Preserve full interval pricing for compare-table display /
         * future UI parity with the pricing cards.
         * ------------------------------------------------------ */
        monthlyPricing:
          plan?.pricing?.monthly ?? {flatFee: 0, perSeat: null},
        annualPricing:
          plan?.pricing?.annual ?? {flatFee: 0, perSeat: null},
      };

      return normalizedPlan;
    });

    console.log("[PricingCompareTable2] Computed normalized plans:", {
      effectiveInterval,
      planKeys: normalizedPlans.map((plan) => plan.key),
      normalizedBillingFields: normalizedPlans.map((plan) => ({
        key: plan.key,
        interval: plan.interval,
        priceId: plan.priceId,
        flatFee: plan.flatFee,
        perSeat: plan.perSeat,
        displayedSeatPrice: plan.displayedSeatPrice,
        pricingDisplay: plan.pricingDisplay,
      })),
    });

    return normalizedPlans;
  }, [pricingPresentation?.plans, effectiveInterval]);

  const compareGroups = Array.isArray(pricingPresentation?.compare)
    ? pricingPresentation?.compare
    : [];

  /* --------------------------------------------------------------
   * Seat-count staging awareness
   *
   * Why:
   * - The compare table summary-row CTA should stay aligned with the
   *   top pricing cards when the user changes seat count locally.
   * - PlanCard2 already uses isSeatCountUpdated to influence CTA state.
   * - Without this, compare-table CTAs can disagree with card CTAs
   *   during the same review session.
   * ------------------------------------------------------------ */
  const currentSubscriptionSeatCount =
    Number.isFinite(Number(currentSubscription?.seats)) ?
      Number(currentSubscription.seats) :
      null;

  const stagedSeatCount =
    Number.isFinite(Number(seatCount)) ?
      Number(seatCount) :
      currentSubscriptionSeatCount;

  const isSeatCountUpdated =
    Number.isFinite(currentSubscriptionSeatCount) &&
    Number.isFinite(stagedSeatCount) &&
    stagedSeatCount !== currentSubscriptionSeatCount;

  /* ----------------------------------------------------------------
   * Diagnostics
   * ---------------------------------------------------------------- */
  useEffect(() => {
    console.log("[PricingCompareTable2] Mounted:", {
      effectiveInterval,
      planKeys: plans.map((plan) => plan.key),
      compareCategoryCount: compareGroups.length,
      context,
    });
  }, []); // mount only

  useEffect(() => {
    console.log("[PricingCompareTable2] Render state update:", {
      effectiveInterval,
      planKeys: plans.map((plan) => plan.key),
      compareCategories: compareGroups.map((group) => group.category),
      context,
      hasCurrentSubscription: !!currentSubscription,
    });
  }, [
    effectiveInterval,
    plans,
    compareGroups,
    context,
    currentSubscription,
  ]);



  const handlePlanAction = useCallback((plan) => {
    const planKey = plan?.key || "";
    const isContact =
      plan?.pricingDisplay === "contact" ||
      plan?.availability?.selfServe === false ||
      plan?.cta?.type === "contact_sales";

    const resolvedPlanPriceId =
      plan?.priceId || getPlanPriceIdForInterval(planKey, effectiveInterval);

    const resolvedDisplayedSeatPrice =
      plan?.displayedSeatPrice != null ?
        Number(plan.displayedSeatPrice) :
        getDisplayedSeatPrice(
          effectiveInterval,
          plan?.perSeat ?? plan?.pricing?.[effectiveInterval]?.perSeat ?? null,
        );

    const resolvedPerSeat =
      Number.isFinite(Number(plan?.perSeat)) ?
        Number(plan.perSeat) :
        (plan?.pricing?.[effectiveInterval]?.perSeat ?? null);

    const resolvedFlatFee =
      Number.isFinite(Number(plan?.flatFee)) ?
        Number(plan.flatFee) :
        (plan?.pricing?.[effectiveInterval]?.flatFee ?? 0);

    const ctaState = getPlanCtaState({
      plan: {
        ...plan,
        interval: plan?.interval || effectiveInterval,
      },
      planKey,
      planPriceId: resolvedPlanPriceId,
      currentSubscription,
      isPublic: context === "public",
      isContactSalesPlan: isContact,
      isSeatCountUpdated,
      planConfigs,
    });

    console.log("[PricingCompareTable2] handlePlanAction:", {
      planKey,
      interval: effectiveInterval,
      normalizedPlanInterval: plan?.interval || null,
      isContact,
      currentSubscriptionStatus: currentSubscription?.status || null,
      currentSubscriptionInterval: currentSubscription?.interval || null,
      currentSubscriptionPriceId: currentSubscription?.priceId || null,
      targetPlanPriceId: resolvedPlanPriceId,
      normalizedDisplayedSeatPrice: resolvedDisplayedSeatPrice,
      normalizedPerSeat: resolvedPerSeat,
      normalizedFlatFee: resolvedFlatFee,
      ctaState,
    });

    if (ctaState.action === "contact_sales") {
      const contactUrl = plan?.cta?.url || plan?.cta?.contact?.url || null;
      if (contactUrl) {
        window.location.href = contactUrl;
      }
      return;
    }

    if (ctaState.action === "current") {
      return;
    }

    if (typeof onPlanSelect !== "function") {
      console.warn("[PricingCompareTable2] onPlanSelect missing:", {
        planKey,
        plan,
        ctaState,
      });
      return;
    }

    onPlanSelect({
      ...plan,
      interval: plan?.interval || effectiveInterval,
      priceId: resolvedPlanPriceId,
      displayedSeatPrice: resolvedDisplayedSeatPrice,
      perSeat: resolvedPerSeat,
      flatFee: resolvedFlatFee,
    });
  }, [
    effectiveInterval,
    onPlanSelect,
    currentSubscription,
    context,
    isSeatCountUpdated,
  ]);

  if (!plans.length || !compareGroups.length) {
    return null;
  }

  return (
    <Box
      component="section"
      id="compare"
      sx={{
        background: "var(--utm-split, #f5f5f5)",
        pt: "14px",
        pb: "18px",
      }}
    >
      <style>
        {`
          .pricing-compare-table2__details > summary::-webkit-details-marker {
            display: none;
          }

          .pricing-compare-table2__details > summary {
            list-style: none;
          }

          .pricing-compare-table2__chevron {
            transform: rotate(0deg);
            transform-origin: center;
          }

          .pricing-compare-table2__details[open] .pricing-compare-table2__chevron {
            transform: rotate(180deg);
          }
        `}
      </style>
      <Box
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          px: "2rem",
        }}
      >
        <Typography
          component="h2"
          sx={{
            textAlign: "center",
            margin: "0 0 10px",
            color: "var(--utm-ink, #0F1A2A)",
            fontWeight: 900,
            letterSpacing: "-0.015em",
            fontSize: "clamp(28px, 4.8vw, 42px)",
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
          }}
        >
          Detailed Feature Comparison
        </Typography>

        <Box sx={{ mt: "10px" }}>
          <Box
            sx={{
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              borderRadius: "22px",
              background: "var(--utm-card, #ffffff)",
              border: "1px solid rgba(15,26,42,.08)",
              boxShadow: "0 10px 28px rgba(15,26,42,.06)",
            }}
          >
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: "980px",
                borderCollapse: "separate",
                borderSpacing: 0,
                background: "var(--utm-card, #ffffff)",
              }}
            >
              <colgroup>
                <col style={{ width: "34%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
              </colgroup>

              <thead>
                <tr>
                  <th
                    scope="col"
                    aria-hidden="true"
                    style={{
                      verticalAlign: "top",
                      background: "#fff",
                      borderBottom: "1px solid rgba(15,26,42,.08)",
                      minWidth: "260px",
                      padding: 0,
                      color: "transparent",
                      fontSize: 0,
                      lineHeight: 0,
                    }}
                  >
                    &nbsp;
                  </th>

                  {PLAN_ORDER.map((planKey) => {
                    const plan = getPlanByKey(plans, planKey);
                    if (!plan) return null;

                    const isContact =
                      plan?.pricingDisplay === "contact" ||
                      plan?.availability?.selfServe === false ||
                      plan?.cta?.type === "contact_sales";

                    const isFeatured = plan?.featured === true;

                    const displayedSeatPrice =
                      plan?.displayedSeatPrice != null ?
                        Number(plan.displayedSeatPrice) :
                        getDisplayedSeatPrice(
                          effectiveInterval,
                          plan?.perSeat ?? plan?.pricing?.[effectiveInterval]?.perSeat ?? null,
                        );

                    const [major, minor] = formatMoneyParts(displayedSeatPrice);
                    const priceMetaText = isContact ? "Contact sales" : "per user / month";

                    return (
                      <th
                        key={`summary-${planKey}`}
                        scope="col"
                        style={{
                          verticalAlign: "top",
                          background: "#fff",
                          borderBottom: "1px solid rgba(15,26,42,.08)",
                          padding: "20px 16px 18px",
                          textAlign: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center",
                            gap: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "var(--utm-ink, #0F1A2A)",
                              fontFamily:
                                "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                              fontSize: "24px",
                              fontWeight: 600,
                              lineHeight: 1.2,
                              letterSpacing: "-0.02em",
                            }}
                          >
                            {plan.name}
                          </Typography>

                          <Box
                            aria-label={isContact ? "Custom pricing" : "Per seat price"}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              gap: "3px",
                              minHeight: "58px",
                            }}
                          >
                            {isContact ? (
                              <Typography
                                component="span"
                                sx={{
                                  color: "var(--utm-ink, #0F1A2A)",
                                  fontFamily:
                                    "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                                  fontSize: "42px",
                                  fontWeight: 700,
                                  lineHeight: 0.95,
                                  letterSpacing: "-0.03em",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Custom
                              </Typography>
                            ) : (
                              <>
                                <Typography
                                  component="span"
                                  sx={{
                                    color: "var(--utm-ink, #0F1A2A)",
                                    fontFamily:
                                      "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                                    fontSize: "24px",
                                    fontWeight: 400,
                                    lineHeight: 1,
                                    position: "relative",
                                    top: "7px",
                                  }}
                                >
                                  $
                                </Typography>

                                <Typography
                                  component="span"
                                  sx={{
                                    color: "var(--utm-ink, #0F1A2A)",
                                    fontFamily:
                                      "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                                    fontSize: "42px",
                                    fontWeight: 700,
                                    lineHeight: 0.95,
                                    letterSpacing: "-0.03em",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {major}
                                </Typography>

                                <Typography
                                  component="span"
                                  sx={{
                                    color: "var(--utm-ink, #0F1A2A)",
                                    fontFamily:
                                      "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    lineHeight: 1,
                                    position: "relative",
                                    top: "9px",
                                  }}
                                >
                                  .{minor}
                                </Typography>
                              </>
                            )}
                          </Box>

                          <Typography
                            sx={{
                              color: "var(--utm-ink, #0F1A2A)",
                              fontFamily:
                                "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                              fontSize: "13px",
                              fontWeight: 400,
                              lineHeight: 1.35,
                            }}
                          >
                            {priceMetaText}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              width: "100%",
                              mt: "6px",
                            }}
                          >
                            {(() => {
                              const planPriceId =
                                plan?.priceId ||
                                getPlanPriceIdForInterval(
                                  plan.key,
                                  effectiveInterval,
                                );

                              const ctaState = getPlanCtaState({
                                plan: {
                                  ...plan,
                                  interval: plan?.interval || effectiveInterval,
                                },
                                planKey: plan.key,
                                planPriceId,
                                currentSubscription,
                                isPublic: context === "public",
                                isContactSalesPlan: isContact,
                                isSeatCountUpdated,
                                planConfigs,
                              });

                              console.log("[PricingCompareTable2] Summary CTA state:", {
                                planKey: plan.key,
                                interval: effectiveInterval,
                                normalizedPlanInterval: plan?.interval || null,
                                currentSubscriptionStatus:
                                  currentSubscription?.status || null,
                                currentSubscriptionInterval:
                                  currentSubscription?.interval || null,
                                currentSubscriptionPriceId:
                                  currentSubscription?.priceId || null,
                                currentSubscriptionPlanName:
                                  currentSubscription?.planName || null,
                                currentSubscriptionPlanKey:
                                  currentSubscription?.planKey || null,
                                targetPlanPriceId: planPriceId,
                                normalizedDisplayedSeatPrice:
                                  plan?.displayedSeatPrice ?? null,
                                normalizedPerSeat: plan?.perSeat ?? null,
                                normalizedFlatFee: plan?.flatFee ?? null,
                                ctaState,
                              });

                              return (
                                <Button
                                  onClick={() => handlePlanAction(plan)}
                                  disabled={ctaState.disabled}
                                  sx={{
                                    width: "72%",
                                    minHeight: "44px",
                                    justifyContent: "center",
                                    borderRadius: "14px",
                                    fontFamily:
                                      "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                                    fontSize: "15px",
                                    fontWeight: 700,
                                    textTransform: "none",
                                    boxShadow: isFeatured
                                      ? "0 8px 18px rgba(72,100,255,.16)"
                                      : "none",
                                    border: "1.5px solid var(--utm-cta-blue, #4864ff)",
                                    background: isFeatured
                                      ? "var(--utm-cta-blue, #4864ff)"
                                      : "#FFFFFF",
                                    color: isFeatured
                                      ? "#FFFFFF"
                                      : "var(--utm-cta-blue, #4864ff)",
                                    "&:hover": {
                                      background: isFeatured
                                        ? "var(--utm-cta-blue-hover, #3648d4)"
                                        : "rgba(72,100,255,.06)",
                                      borderColor: isFeatured
                                        ? "var(--utm-cta-blue-hover, #3648d4)"
                                        : "var(--utm-cta-blue, #4864ff)",
                                      boxShadow: isFeatured
                                        ? "0 10px 22px rgba(72,100,255,.20)"
                                        : "none",
                                    },
                                    "&.Mui-disabled": {
                                      background: "#e0e0e0",
                                      color: "#9e9e9e",
                                      borderColor: "#e0e0e0",
                                    },
                                  }}
                                >
                                  {ctaState.label}
                                </Button>
                              );
                            })()}
                          </Box>
                        </Box>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {compareGroups.map((group, groupIndex) => (
                  <tr key={`group-${group.category}`}>
                    <td
                      colSpan={4}
                      style={{
                        padding: 0,
                        borderBottom: "1px solid rgba(15,26,42,.06)",
                        background: "#fff",
                      }}
                    >
                      <details
                        open={groupIndex === 0}
                        className="pricing-compare-table2__details"
                      >
                        <summary
                          style={{
                            listStyle: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "18px 20px",
                            background: "#fff",
                            color: "var(--utm-ink, #0F1A2A)",
                            fontFamily:
                              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                            fontSize: "16px",
                            fontWeight: 600,
                            lineHeight: 1.35,
                          }}
                        >
                          <span>{group.category}</span>
                          <span aria-hidden="true">
                            <ChevronIcon />
                          </span>
                        </summary>

                        <Box
                          component="table"
                          sx={{
                            width: "100%",
                            borderCollapse: "collapse",
                            tableLayout: "fixed",
                            background: "#fff",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "34%" }} />
                            <col style={{ width: "22%" }} />
                            <col style={{ width: "22%" }} />
                            <col style={{ width: "22%" }} />
                          </colgroup>

                          <tbody>
                            {(Array.isArray(group.rows) ? group.rows : []).map((row, rowIndex) => (
                              <tr key={row.featureId}>
                                <th
                                  scope="row"
                                  style={{
                                    padding: "14px 16px",
                                    borderTop: "1px solid rgba(15,26,42,.06)",
                                    background:
                                      rowIndex % 2 === 1 ? "rgba(15,26,42,.025)" : "#fff",
                                    textAlign: "left",
                                    color: "#374151",
                                    fontFamily:
                                      "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {row.tooltip ? (
                                    <Tooltip
                                      title={row.tooltip}
                                      arrow
                                      placement="top-start"
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            backgroundColor: "#111827",
                                            color: "#FFFFFF",
                                            fontFamily:
                                              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            lineHeight: 1.45,
                                            padding: "10px 12px",
                                            borderRadius: "10px",
                                            boxShadow: "0 12px 28px rgba(15,26,42,.18)",
                                            maxWidth: "320px",
                                          },
                                        },
                                        arrow: {
                                          sx: {
                                            color: "#111827",
                                          },
                                        },
                                      }}
                                    >
                                      <span
                                        tabIndex={0}
                                        style={{
                                          color: "#64748b",
                                          textDecorationLine: "underline",
                                          textDecorationStyle: "dotted",
                                          textDecorationColor: "rgba(100,116,139,.9)",
                                          textUnderlineOffset: "3px",
                                          cursor: "help",
                                          outline: "none",
                                        }}
                                      >
                                        {row.label}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <span>{row.label}</span>
                                  )}
                                </th>

                                {PLAN_ORDER.map((planKey) => {
                                  const hasFeature = row?.[planKey] === true;

                                  return (
                                    <td
                                      key={`${row.featureId}-${planKey}`}
                                      style={{
                                        padding: "14px 16px",
                                        borderTop: "1px solid rgba(15,26,42,.06)",
                                        background:
                                          rowIndex % 2 === 1 ? "rgba(15,26,42,.025)" : "#fff",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                      }}
                                    >
                                      {hasFeature ? (
                                        <span
                                          aria-hidden="true"
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 18,
                                            height: 18,
                                            color: "#16a34a",
                                          }}
                                        >
                                          <CheckIcon />
                                        </span>
                                      ) : (
                                        <span
                                          aria-hidden="true"
                                          style={{
                                            display: "inline-block",
                                            width: 18,
                                            height: 18,
                                            lineHeight: 1,
                                          }}
                                        />
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </Box>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

PricingCompareTable2.propTypes = {
  pricingPresentation: PropTypes.shape({
    plans: PropTypes.array,
    compare: PropTypes.array,
  }),
  pricingInterval: PropTypes.oneOf(["monthly", "annual"]),
  onPlanSelect: PropTypes.func,
  currentSubscription: PropTypes.shape({
    priceId: PropTypes.string,
    status: PropTypes.string,
    seats: PropTypes.number,
  }),
  context: PropTypes.oneOf(["public", "account"]),
  seatCount: PropTypes.number,
};

export default React.memo(PricingCompareTable2);